

import React, { useState, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { XIcon } from './icons';

interface TutorialStep {
  target: string;
  path: string;
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const steps: TutorialStep[] = [
  {
    target: '[data-tutorial-id="sidebar"]',
    path: '/dashboard',
    title: 'Main Navigation',
    content: "Use the sidebar to navigate between the main sections of your project, like the Dashboard, Tasks, and Data.",
    position: 'right',
  },
  {
    target: '[data-tutorial-id="project-switcher"]',
    path: '/dashboard',
    title: 'Switch Projects',
    content: "If you're working on multiple projects, you can easily switch between them here or create a new one.",
    position: 'bottom',
  },
  {
    target: '[data-tutorial-id="dashboard-view"]',
    path: '/dashboard',
    title: 'Project Dashboard',
    content: "This is your project's command center. Get a quick overview of recent submissions, task progress, and participant activity.",
    position: 'bottom',
  },
  {
    target: '[data-tutorial-id="filter-bar"]',
    path: '/data',
    title: 'Filter Your Data',
    content: "When viewing submissions, use these controls to filter by media type, search for specific notes or tags, and switch between grid and list views.",
    position: 'bottom',
  },
  {
    target: '[data-tutorial-id="add-task-button"]',
    path: '/tasks',
    title: 'Create Tasks',
    content: "This is where you'll create and manage tasks for your participants to complete.",
    position: 'left',
  },
  {
    target: '[data-tutorial-id="role-switcher"]',
    path: '/tasks',
    title: 'Preview as Participant',
    content: "You can switch to the Participant view at any time to see what your participants experience.",
    position: 'left',
  },
];

interface ResearcherOnboardingProps {
  onComplete: () => void;
}

interface HighlightStyle {
  top: number;
  left: number;
  width: number;
  height: number;
}

const ResearcherOnboarding: React.FC<ResearcherOnboardingProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [style, setStyle] = useState<HighlightStyle | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updateHighlight = () => {
    const step = steps[currentStep];
    const element = document.querySelector(step.target) as HTMLElement;

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

      const timer = setTimeout(() => {
        const rect = element.getBoundingClientRect();
        setStyle({
          width: rect.width + 16,
          height: rect.height + 16,
          top: rect.top - 8,
          left: rect.left - 8,
        });
      }, 300);
      return () => clearTimeout(timer);
    } else {
      console.warn(`Tutorial target not found: ${step.target}`);
      // Skip to next step if element not found, or finish if it's the last one
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        onComplete();
      }
    }
  };

  useLayoutEffect(() => {
    const step = steps[currentStep];
    if (step.path !== location.pathname) {
      navigate(step.path);
      // Defer highlight update until after view switch
      return;
    }

    const cleanup = updateHighlight();
    window.addEventListener('resize', updateHighlight);
    return () => {
      if (cleanup) cleanup();
      window.removeEventListener('resize', updateHighlight);
    }
  }, [currentStep, location.pathname]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const stepConfig = steps[currentStep];
  const tooltipPositionStyle = (): React.CSSProperties => {
    if (!style) return { opacity: 0, visibility: 'hidden' };

    const tooltipHeight = (tooltipRef.current && tooltipRef.current.offsetHeight) || 150;
    const tooltipWidth = (tooltipRef.current && tooltipRef.current.offsetWidth) || 300;
    const spacing = 16;

    let top = style.top + style.height / 2 - tooltipHeight / 2;
    let left = style.left + style.width / 2 - tooltipWidth / 2;

    switch (stepConfig.position) {
      case 'top':
        top = style.top - tooltipHeight - spacing;
        break;
      case 'bottom':
        top = style.top + style.height + spacing;
        break;
      case 'left':
        left = style.left - tooltipWidth - spacing;
        break;
      case 'right':
        left = style.left + style.width + spacing;
        break;
      default:
        top = style.top + style.height + spacing;
    }

    if (top < spacing) top = spacing;
    if (left < spacing) left = spacing;
    if (left + tooltipWidth > window.innerWidth - spacing) left = window.innerWidth - tooltipWidth - spacing;
    if (top + tooltipHeight > window.innerHeight - spacing) top = window.innerHeight - tooltipHeight - spacing;

    return { top, left, opacity: 1, visibility: 'visible' };
  }

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute transition-all duration-300 pointer-events-none"
        style={style ? {
          ...style,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
          borderRadius: '0.75rem',
        } : { opacity: 0 }}
      />

      <div
        ref={tooltipRef}
        className="absolute bg-white rounded-lg shadow-2xl w-80 p-5 animate-fade-in-down transition-all duration-300"
        style={tooltipPositionStyle()}
      >
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-bold text-text-ink">{stepConfig.title}</h3>
          <button onClick={onComplete} className="p-1 -mr-2 -mt-2 rounded-full text-text-faint hover:bg-neutral-100 active:bg-neutral-200">
            <XIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-text-muted text-sm leading-relaxed">{stepConfig.content}</p>
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm font-medium text-text-muted">
            {currentStep + 1} / {steps.length}
          </span>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <button
                onClick={handlePrev}
                className="bg-white border border-app-border text-text-ink font-semibold py-1.5 px-3 rounded-md text-sm hover:bg-neutral-100 active:bg-neutral-200"
              >
                Previous
              </button>
            )}
            <button
              onClick={handleNext}
              className="bg-action text-white text-sm font-medium py-1.5 px-3 rounded-lg hover:bg-action/90 active:scale-95 transition-colors"
            >
              {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearcherOnboarding;
